<template>
  <div class="insights-shell card">
    <!-- Branded header -->
    <div class="insights-header">
      <div class="title-wrap">
        <i class="bi bi-lightbulb"></i>
        <h5 class="title">{{ $t('zones.insights.title') }}</h5>
      </div>

      <div class="header-controls">
        <button class="stora-btn-secondary" @click="downloadPdf" :disabled="!reportHtml">
          <i class="bi bi-file-earmark-arrow-down"></i><span>PDF</span>
        </button>
        <button class="icon-close" @click="$emit('close')" :aria-label="$t('zones.insights.close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="card-body insights-body">
      <p
        v-if="reportHtml && selectedDuration == 365 && dateRangeMode == 'duration'"
        class="hint"
      >
        <i18n-t keypath="zones.insights.hint" tag="i" scope="global">
          <template #important><b>{{ $t('zones.insights.important') }}</b></template>
        </i18n-t>
      </p>

      <div v-if="reportHtml" class="report-container" v-html="reportHtml" ref="reportContainer"></div>

      <div v-else class="loader-overlay">
        <p class="loading-copy"><i>{{ $t('zones.insights.loading') }}</i></p>
        <div class="loader"></div>
      </div>
    </div>
  </div>
</template>

<script>

import image from "./icons/logo_storalytic.png"

// @ts-ignore
import html2pdf  from 'html2pdf.js'

export default {
  name: 'StoreAnalyticsByAllen',
  emits: ['close'],
  props: { 
    reportHtml: String,
    selectedDuration: Number,
    dateRangeMode: String,
    origin: String,
    zoneName: String
  },
  methods: {
    downloadPdf() {
      // grab the node
      const element = this.$refs.reportContainer
      if (!element) return

      const durationMap = {
        1:    'yesterday',
        7:    'past_7_days',
        30:   'past_30_days',
        365:  'past_365_days',
        1000: 'this_week',
        2000: 'this_month',
        3000: 'last_week',
        4000: 'last_month'
      }
      const durStr = durationMap[this.selectedDuration] || this.selectedDuration

      let filename = `Storalytic_${this.origin}_Insights`
      if (this.origin === 'Zone' && this.zoneName) {
        // replace spaces with underscores
        const safeZone = this.zoneName.replace(/\s+/g, '_')
        filename += `_${safeZone}`
      }
      filename += `_${durStr}.pdf`

      const opt = {
        margin:       [0.75, 0.5, 0.75, 0.5],  // inches: [top, left, bottom, right]
        filename:     filename,
        image:        { type: "jpeg", quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true },
        // compress: jsPDF 4 no longer compresses PNGs by default; without it the
        // header logo alone makes the PDF ~118 MB instead of ~2 MB.
        jsPDF:        { unit: "in", format: "a4", orientation: "portrait", compress: true },
        pagebreak: {
          mode: ['avoid-all', 'css'],      // respect both CSS and avoidance
          avoid: ['.zone-card']            // do not cut inside these
        }
      }

      // launch the download
      const worker = html2pdf()
        .set(opt)
        .from(element)
        .toPdf()
        
      worker.get('pdf')
        .then(pdf => {
          const pageCount = pdf.internal.getNumberOfPages()
          const w = pdf.internal.pageSize.getWidth()
          const h = pdf.internal.pageSize.getHeight()
          const margin     = 0.5   // same as your top margin in inches
          const imgWidth   = 1.0   // inches
          const imgHeight  = (imgWidth *  /* at 300dpi your png is WxH*/  /* e.g. 100px height / 100px width*/ 1)

          // build a header template once
          const base = this.origin === 'Store'
            ? this.$t('zones.insights.pdfStore')
            : this.$t('zones.insights.pdfZone', { zone: this.zoneName })  // use zoneName for Zone
          const headerText = `${base} — ${durStr}`

          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i)

            // draw a colored header bar at the very top
            pdf.setDrawColor(79, 126, 179);    // #4F7EB3 border
            pdf.setFillColor(79, 126, 179);    // same fill
            pdf.rect(0, 0, w, 0.5, 'FD');      // x, y, width, height in inches

            // now the header text in white, left-aligned, 0.3" from left edge, 0.35" down
            pdf.setFontSize(10);
            pdf.setTextColor(255, 255, 255);
            pdf.text(headerText, 0.3, 0.35, { align: 'left' });

            // addImage(src, format, x, y, width, height)
            pdf.addImage(
              image,       // the imported URL becomes a base64 data: URI
              'PNG',
              w - margin - imgWidth, //x
              0.05, //y
              imgWidth,
              imgHeight
            )

            // footer: page X of Y, centered at 0.5" from bottom
            pdf.setFontSize(8);
            pdf.setTextColor(100);
            pdf.text(
              this.$t('zones.insights.pdfPage', { i, n: pageCount }),
              w / 2,
              h - 0.3,
              { align: 'center' }
            )
          }
          pdf.save(filename)
        })
    }
  }
}
</script>

<style scoped>
/* Shell / card */
.insights-shell{
  border:1px solid #e6edf3;
  border-radius:12px;
  box-shadow:0 3px 12px rgba(13,38,76,.08);
  overflow:hidden;
}

/* Header */
.insights-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 16px;
  background:#f7f9fb;
  border-bottom:1px solid #e6edf3;
}
.title-wrap{
  display:flex; align-items:center; gap:10px;
}
.title-wrap .bi-lightbulb{ color:#4F7EB3; font-size:1.1rem; }
.title{
  margin:0; font-size:1.1rem; font-weight:700; color:#4F7EB3;
}

/* Controls */
.header-controls{ display:flex; align-items:center; gap:8px; }
.stora-btn-secondary{
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:8px;
  background:#4F7EB3; color:#fff; border:0; cursor:pointer;
  transition: background .2s ease, transform .02s ease;
  font-size:.9rem;
}
.stora-btn-secondary:hover{ background:#B2C149; }
.stora-btn-secondary:disabled{ opacity:.6; cursor:not-allowed; }
.icon-close{
  display:inline-flex; align-items:center; justify-content:center;
  width:36px; height:36px; border-radius:8px; border:1px solid #e6edf3;
  background:#fff; color:#425461; cursor:pointer; transition:background .2s ease;
}
.icon-close:hover{ background:#f0f4f8; }

/* Body */
.insights-body{ padding:16px; }
.hint{
  font-size:.85rem; color:#425461; margin:0 0 8px 2px;
}

/* Report content */
:deep(.report-container){
  display:grid; gap:14px; padding:2px;
}
/* The grounded reading summaries render bare <h3>/<h4>/<p>/<ul> directly into
   .report-container (no .zone-card wrapper), so they need their own branded
   typography — otherwise headings fall back to plain black. Mirrors the brand
   palette used elsewhere (blue #4F7EB3 titles, lime #B2C149 emphasis). */
:deep(.report-container){
  background:#fff; border-radius:10px; border:1px solid #eef2f6;
  box-shadow:0 2px 8px rgba(0,0,0,.05); padding:16px; color:#425461;
}
:deep(.report-container h3){
  margin:0 0 10px; font-size:1.05rem; font-weight:700; color:#4F7EB3;
  border-bottom:2px solid #f0f2f6; padding-bottom:10px;
}
:deep(.report-container h4){
  margin:16px 0 4px; font-size:.95rem; font-weight:700; color:#425461;
}
:deep(.report-container p){ margin:.45rem 0; font-size:.92rem; line-height:1.55; }
:deep(.report-container strong){ color:#B2C149; }
:deep(.report-container ul){ list-style:disc inside; margin:.55rem 0; padding-left:.2rem; }
:deep(.report-container li){ margin-bottom:.4rem; font-size:.92rem; }
:deep(.zone-card){
  background:#fff; border-radius:10px; border:1px solid #eef2f6;
  box-shadow:0 2px 8px rgba(0,0,0,.05);
  padding:16px; font-size:.92rem; color:#425461;
}
:deep(.zone-card strong){ color:#B2C149; }
:deep(.zone-card h2){
  margin:0; font-size:1rem; color:#4F7EB3;
  border-bottom:2px solid #f0f2f6; padding:8px 0 10px;
}
:deep(.zone-card h3){
  margin:0; font-size:.98rem; color:#425461;
  border-bottom:1px solid #f0f2f6; padding:8px 0 8px;
}
:deep(.zone-card ul){ list-style:disc inside; margin:.6rem 0; }
:deep(.zone-card li){ margin-bottom:.4rem; }

/* Loader */
.loader-overlay{
  position:relative; min-height:140px;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  background:linear-gradient(0deg, rgba(245,247,250,.6), rgba(245,247,250,.6));
  border:1px dashed #e6edf3; border-radius:10px;
}
.loading-copy{ color:#8899a6; font-size:.9rem; margin:0 0 10px; }

.loader{
  width:24px; padding:2px; aspect-ratio:1;
  border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn); } }
</style>